import { Button } from '@rmwc/button';
import { CircularProgress } from '@rmwc/circular-progress';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@rmwc/dialog';
import { Select } from '@rmwc/select';
import { TextField } from '@rmwc/textfield';
import { Typography } from '@rmwc/typography';
import { Autocomplete } from 'components';
import {
  useAccountSearchLazyQuery,
  useInsertAccountSubscriptionMutation,
  usePlanSearchLazyQuery,
} from 'generated/graphql';
import { useApplyAccountProvisioningDefaults, useSeedGlobalEmisisonPreCalculations } from 'services';
import moment from 'moment';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { queue } from 'utilities';
import { v4 as uuidv4 } from 'uuid';

import { SubscriptionStatusEnum } from 'interfaces/enums';
import styles from './CustomModal.module.scss';

export const AddAccountSubscription = ({ open, setOpen }: any) => {
  const history = useHistory();
  const { formState, control, register, handleSubmit, setError, clearErrors, reset, setValue, watch } = useForm();
  const { errors } = formState;
  const [Account, Plan] = watch(['Account', 'Plan']);
  const [searchAccounts, { data: AccountSearchData, loading: AccountSearchLoading }] = useAccountSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [searchPlans, { data: PlanSearchData, loading: PlanSearchLoading }] = usePlanSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [insertFn] = useInsertAccountSubscriptionMutation();
  const { mutateAsync: applyAccountProvisioningDefaults } = useApplyAccountProvisioningDefaults();
  const { mutateAsync: seedGlobalEmissionsPreCalculations } = useSeedGlobalEmisisonPreCalculations();
  const [createdSubscription, setCreatedSubscription] = React.useState<{
    accountId: string;
    subscriptionId: string;
  } | null>(null);
  const statusFields = [];
  for (let key in Object.keys(SubscriptionStatusEnum)) {
    if (SubscriptionStatusEnum[key]) {
      statusFields.push({ label: SubscriptionStatusEnum[key], value: Number(key) });
    }
  }

  const lastDetail = JSON.parse(sessionStorage.getItem('lastDetail'));
  let accountDefaultValue = null as any;
  if (lastDetail?.data['AccountId'] && lastDetail?.data['Label']) {
    accountDefaultValue = lastDetail.data;
  }

  React.useEffect(() => {
    if (accountDefaultValue) {
      setValue('Account', accountDefaultValue);
    } else {
      searchAccounts({ variables: { search: '' } });
    }
    searchPlans({ variables: { search: '' } });
    // eslint-disable-next-line
  }, ['open']);

  const onSubmit = async (data: any) => {
    let subscription = createdSubscription;

    try {
      if (subscription === null) {
        const accountId = data.Account.AccountId;
        const subscriptionId = uuidv4();
        const variables = {
          object: {
            ...data,
            AccountId: accountId,
            PlanId: data.Plan.PlanId,
            Status: Number(data.Status),
            SubscriptionId: subscriptionId,
            CreatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
            UpdatedOn: moment.utc().format('YYYY-MM-DDTHH:mm:00'),
          },
        };
        delete variables.object.Account;
        delete variables.object.Plan;
        const { errors } = await insertFn({ variables });

        if (errors?.length > 0) {
          throw new Error(errors[0].message);
        }

        subscription = { accountId, subscriptionId };
        setCreatedSubscription(subscription);
      }

      await applyAccountProvisioningDefaults({ accountId: subscription.accountId });

      // Seed the global emission for new account
      try {
        await seedGlobalEmissionsPreCalculations({ accountId: subscription.accountId });
      } catch (error) {
        console.error('seedGlobalEmisisonPreCalculations failed', { accountId: subscription.accountId, error });
      }

      queue.clearAll();
      queue.notify({
        icon: 'done',
        title: 'Success!',
        body: `Account Subscription Added!`,
      });

      const subscriptionId = subscription.subscriptionId;
      setCreatedSubscription(null);
      setOpen(false);
      setTimeout(() => {
        // window.location.reload();
        history.push('/app/detail/accountsSubscription/' + subscriptionId);
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to add account subscription.';
      const message = subscription
        ? `The subscription was created, but its default setup failed. Click Retry setup to try again. ${errorMessage}`
        : errorMessage;

      queue.clearAll();
      queue.notify({
        icon: 'priority_high',
        title: 'Oh oh, error!',
        body: message,
      });
      setError('api', { type: 'manual', message });
    }
  };

  return (
    <div className={styles.customModal}>
      <Dialog
        className={styles.addSubscription}
        open={open}
        onClose={(evt) => {
          setCreatedSubscription(null);
          reset();
          clearErrors();
          if (open) {
            setOpen(false);
          }
        }}>
        <form onSubmit={handleSubmit(onSubmit)} data-private autoComplete="off">
          <DialogTitle>Add Account Subscription</DialogTitle>
          <DialogContent>
            <div className={styles.row}>
              <Controller
                name="Account"
                control={control}
                rules={{
                  required: 'Please select an account',
                }}
                render={({ field }) => (
                  <Autocomplete
                    label="Account"
                    data={AccountSearchData?.dw_Accounts || []}
                    labelKey="Label"
                    valueKey="AccountId"
                    loading={AccountSearchLoading}
                    getData={searchAccounts}
                    isDisabled={accountDefaultValue}
                    value={Account}
                    callback={(item) => {
                      field.onChange(item.FullData);
                      setTimeout(() => {
                        field.onBlur();
                      }, 0);
                    }}
                  />
                )}
              />
              {errors?.Account?.message ? (
                <div className={styles.error}>
                  <Typography use="caption">{errors?.Account.message}</Typography>
                </div>
              ) : null}
            </div>

            <div className={styles.row}>
              <Controller
                name="Plan"
                control={control}
                rules={{
                  required: 'Please select a plan',
                }}
                render={({ field }) => (
                  <Autocomplete
                    label="Plan"
                    data={PlanSearchData?.dw_Plans || []}
                    labelKey="Label"
                    valueKey="PlanId"
                    loading={PlanSearchLoading}
                    getData={searchPlans}
                    value={Plan}
                    callback={(item) => {
                      field.onChange(item.FullData);
                      setTimeout(() => {
                        field.onBlur();
                      }, 0);
                    }}
                  />
                )}
              />
              {errors?.Plan?.message ? (
                <div className={styles.error}>
                  <Typography use="caption">{errors?.Plan.message}</Typography>
                </div>
              ) : null}
            </div>

            <div className={styles.row}>
              <TextField
                label="Payment Customer Ref"
                name="PaymentCustomerRef"
                outlined
                floatLabel={true}
                {...register('PaymentCustomerRef')}
              />
              {errors.PaymentCustomerRef && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a payment customer ref</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <TextField
                label="Balance"
                name="Balance"
                type="number"
                outlined
                floatLabel={true}
                defaultValue={0}
                {...register('Balance')}
              />
              {errors.Balanceabel && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a Balance</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <Controller
                name="BalanceAsAt"
                control={control}
                defaultValue={moment().format('YYYY-MM-DD')}
                render={({ field }) => (
                  <TextField
                    label="Balance As At"
                    name="BalanceAsAt"
                    type="date"
                    outlined
                    autoFocus
                    floatLabel={true}
                    {...field}
                  />
                )}
              />
              {errors.CommerceOn && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a valid Balance As At date DD/MM/YYYY</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <Controller
                name="CommerceOn"
                control={control}
                defaultValue={moment().format('YYYY-MM-DD')}
                render={({ field }) => (
                  <TextField
                    label="Commerce On"
                    name="CommerceOn"
                    type="date"
                    outlined
                    autoFocus
                    floatLabel={true}
                    {...field}
                  />
                )}
              />
              {errors.CommerceOn && (
                <div className={styles.error}>
                  <Typography use="caption">Please enter a valid Commerce On date DD/MM/YYYY</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              <Controller
                name="Status"
                control={control}
                rules={{
                  required: true,
                }}
                defaultValue={'0'}
                render={({ field }) => (
                  <Select
                    label="Status"
                    name="Status"
                    outlined
                    required
                    options={statusFields}
                    defaultValue={'0'}
                    {...field}
                  />
                )}
              />
              {errors.Status && (
                <div className={styles.error}>
                  <Typography use="caption">Please select a valid status</Typography>
                </div>
              )}
            </div>

            <div className={styles.row}>
              {errors.api && (
                <div className={styles.error} style={{ marginBottom: '16px' }}>
                  <Typography use="caption">{errors.api.message}</Typography>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'space-between' }}>
            <Button
              type="button"
              label="Close"
              disabled={formState.isSubmitting}
              outlined
              onClick={() => {
                setCreatedSubscription(null);
                setOpen(false);
                clearErrors();
                reset();
              }}
            />
            <Button
              raised
              disabled={formState.isSubmitting}
              label={formState.isSubmitting ? 'Saving' : createdSubscription ? 'Retry setup' : 'Save'}
              icon={formState.isSubmitting ? <CircularProgress theme="secondary" /> : null}
              onClick={() => {
                clearErrors();
              }}
              type="submit"
            />
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default AddAccountSubscription;
